import React, { useState } from 'react';
import { PROJECTS } from '../../data/config';
import { Github, ExternalLink, Star, GitFork, Users } from 'lucide-react';

const Projects = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...new Set(PROJECTS.map(p => p.category))];

  const filteredProjects = filter === 'All' 
    ? PROJECTS 
    : PROJECTS.filter(p => p.category === filter);

  return (
    <section id="projects" className="section-container">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Featured Projects</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cream-200 to-cream-300 mx-auto mb-6 rounded-full" style={{ boxShadow: '0 0 20px rgba(255, 232, 200, 0.5)' }}></div>
          <p className="text-cream-200 text-lg max-w-2xl mx-auto" style={{ opacity: 0.9 }}>
            Open-source tools and platforms that power modern infrastructure
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '2rem',
                fontWeight: '600',
                fontFamily: 'Fredoka, sans-serif',
                transition: 'all 0.3s ease',
                ...(filter === category ? {
                  background: 'linear-gradient(135deg, rgba(255, 232, 200, 0.3) 0%, rgba(237, 197, 143, 0.4) 100%)',
                  color: '#ffe8c8',
                  boxShadow: '0 0 30px rgba(255, 232, 200, 0.4)',
                } : {
                  background: 'rgba(255, 232, 200, 0.05)',
                  color: '#edc58f',
                })
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="glass-card overflow-hidden group"
            >
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 right-4">
                  <span 
                    className="px-3 py-1 text-xs font-semibold rounded-full"
                    style={{
                      background: 'rgba(20, 12, 8, 0.8)',
                      backdropFilter: 'blur(10px)',
                      color: '#ffdaa8',
                      border: '1px solid rgba(255, 232, 200, 0.2)',
                    }}
                  >
                    {project.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Title */}
                <h3 className="text-xl font-bold text-cream-100 mb-2 group-hover:text-cream-200 transition-colors">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-cream-200 text-sm mb-4 line-clamp-3" style={{ opacity: 0.8 }}>
                  {project.description}
                </p>

                {/* Metrics */}
                <div className="flex items-center gap-4 mb-4 text-sm" style={{ color: '#edc58f' }}>
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    {project.metrics.stars}
                  </span>
                  <span className="flex items-center gap-1">
                    <GitFork className="w-4 h-4" />
                    {project.metrics.forks}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {project.metrics.users}
                  </span>
                </div>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.techStack.slice(0, 4).map((tech, i) => (
                    <span 
                      key={i} 
                      className="px-2 py-1 text-xs rounded"
                      style={{
                        background: 'rgba(255, 232, 200, 0.1)',
                        color: '#edc58f',
                        border: '1px solid rgba(255, 232, 200, 0.15)',
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                  {project.techStack.length > 4 && (
                    <span 
                      className="px-2 py-1 text-xs rounded"
                      style={{
                        background: 'rgba(255, 232, 200, 0.1)',
                        color: '#edc58f',
                        border: '1px solid rgba(255, 232, 200, 0.15)',
                      }}
                    >
                      +{project.techStack.length - 4}
                    </span>
                  )}
                </div>

                {/* Links */}
                <div className="flex gap-3">
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all"
                    style={{
                      background: 'rgba(255, 232, 200, 0.1)',
                      color: '#ffe8c8',
                      fontWeight: '600',
                      fontFamily: 'Fredoka, sans-serif',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 232, 200, 0.2)';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 232, 200, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 232, 200, 0.1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <Github className="w-4 h-4" />
                    Code
                  </a>
                  <a
                    href={project.demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 232, 200, 0.25) 0%, rgba(237, 197, 143, 0.35) 100%)',
                      color: '#ffe8c8',
                      fontWeight: '600',
                      fontFamily: 'Fredoka, sans-serif',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 232, 200, 0.35) 0%, rgba(237, 197, 143, 0.45) 100%)';
                      e.currentTarget.style.boxShadow = '0 0 25px rgba(255, 232, 200, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 232, 200, 0.25) 0%, rgba(237, 197, 143, 0.35) 100%)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Demo
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
